/**
 * Money on screen — a FAÇADE over `features/allocations/lib/money.ts`.
 *
 * That module is canonical: the allocation table depends on it, and the table and this lane's
 * header sit on the same screen showing the same figures. Two correct implementations of
 * minor-unit arithmetic on one page is a latent disagreement waiting for an edge case, so all
 * the arithmetic here delegates and only the API shape (and the per-currency grouping) is
 * local. PLANNING-CONTRACT §2 permits integer minor units, which is what it uses.
 *
 * What the façade keeps is the three-state rule, because it is what the screen must never blur:
 *
 *   null   → Unbudgeted — nobody has said what this costs
 *   "0.00" → a budget of zero — somebody has said it costs nothing
 *   "1.00" → a budget of one
 */
import {
  UNBUDGETED_LABEL as ALLOC_UNBUDGETED_LABEL,
  fromMinor,
  isMoneyString,
  sumMinor,
  toMinor,
  type Minor,
} from '@/features/allocations/lib/money';

export const UNBUDGETED_LABEL = ALLOC_UNBUDGETED_LABEL;

export function isMoney(value: string | null | undefined): value is string {
  return typeof value === 'string' && isMoneyString(value);
}

/** A decimal string as integer minor units. Throws on anything unparseable. */
export function toMinorUnits(amount: string): Minor {
  if (!isMoney(amount)) throw new Error(`Not a money amount: ${amount}`);
  return toMinor(amount);
}

export function fromMinorUnits(minor: Minor): string {
  return fromMinor(minor);
}

/** Exact addition of decimal strings. Unparseable entries are skipped, not coerced to zero. */
export function sumAmounts(amounts: Array<string | null | undefined>): string {
  const minors: Minor[] = [];
  for (const amount of amounts) if (isMoney(amount)) minors.push(toMinor(amount));
  return fromMinor(sumMinor(minors));
}

/** `a - b`, exactly. Used for planned minus approved funding, which is a real, signed number. */
export function subtractAmounts(a: string | null | undefined, b: string | null | undefined): string {
  return fromMinor((isMoney(a) ? toMinor(a) : 0) - (isMoney(b) ? toMinor(b) : 0));
}

export function isZeroAmount(amount: string | null | undefined): boolean {
  return isMoney(amount) && toMinor(amount) === 0;
}

export interface MoneyFormatOptions {
  /** `$250K` instead of `$250,000`, for Gantt bars and tiles where space is the constraint. Never in a table. */
  compact?: boolean;
  /** What to render when the amount is null. Defaults to "Unbudgeted". */
  unbudgeted?: string;
}

const CURRENCY = /^[A-Z]{3}$/;
let settingsCurrency = 'USD';

/**
 * ISO 4217 alphabetic currency codes (#78, thread 4090215163). `parseMoneyInput` strips a
 * leading currency prefix, but only from this allow-list plus the four symbols below — never
 * an arbitrary alphabetic run. Before this fix a bare `/^[^\d,.-]+/` stripped ANY leading
 * non-digit text as though it were a currency symbol, so "abc123" parsed as 123: a typo landed
 * as a real amount with no error. This list is deliberately the standard active ISO 4217 set,
 * not just the currencies the app happens to display today, so a valid code is never rejected
 * as a typo just because nobody has configured that currency yet.
 */
const ISO_4217_CODES = new Set([
  'AED', 'AFN', 'ALL', 'AMD', 'ANG', 'AOA', 'ARS', 'AUD', 'AWG', 'AZN',
  'BAM', 'BBD', 'BDT', 'BGN', 'BHD', 'BIF', 'BMD', 'BND', 'BOB', 'BRL',
  'BSD', 'BTN', 'BWP', 'BYN', 'BZD', 'CAD', 'CDF', 'CHF', 'CLP', 'CNY',
  'COP', 'CRC', 'CUP', 'CVE', 'CZK', 'DJF', 'DKK', 'DOP', 'DZD', 'EGP',
  'ERN', 'ETB', 'EUR', 'FJD', 'FKP', 'GBP', 'GEL', 'GHS', 'GIP', 'GMD',
  'GNF', 'GTQ', 'GYD', 'HKD', 'HNL', 'HTG', 'HUF', 'IDR', 'ILS', 'INR',
  'IQD', 'IRR', 'ISK', 'JMD', 'JOD', 'JPY', 'KES', 'KGS', 'KHR', 'KMF',
  'KPW', 'KRW', 'KWD', 'KYD', 'KZT', 'LAK', 'LBP', 'LKR', 'LRD', 'LSL',
  'LYD', 'MAD', 'MDL', 'MGA', 'MKD', 'MMK', 'MNT', 'MOP', 'MRU', 'MUR',
  'MVR', 'MWK', 'MXN', 'MYR', 'MZN', 'NAD', 'NGN', 'NIO', 'NOK', 'NPR',
  'NZD', 'OMR', 'PAB', 'PEN', 'PGK', 'PHP', 'PKR', 'PLN', 'PYG', 'QAR',
  'RON', 'RSD', 'RUB', 'RWF', 'SAR', 'SBD', 'SCR', 'SDG', 'SEK', 'SGD',
  'SHP', 'SLE', 'SOS', 'SRD', 'SSP', 'STN', 'SYP', 'SZL', 'THB', 'TJS',
  'TMT', 'TND', 'TOP', 'TRY', 'TTD', 'TWD', 'TZS', 'UAH', 'UGX', 'USD',
  'UYU', 'UZS', 'VES', 'VND', 'VUV', 'WST', 'XAF', 'XCD', 'XOF', 'XPF',
  'YER', 'ZAR', 'ZMW', 'ZWL',
]);

/** A single-character currency symbol `parseMoneyInput` also treats as a prefix, alongside an
 * ISO 4217 alphabetic code. Deliberately small — anything else alphabetic is a typo, not a
 * symbol this app recognises. */
const CURRENCY_SYMBOLS = new Set(['$', '€', '£', '¥']);

/**
 * Matches a leading currency prefix `parseMoneyInput` is allowed to strip: one of the four
 * symbols above, or a 3-letter ISO code (case-insensitive — any whitespace between the code and
 * the digits was already removed by the caller's `\s+` strip, so "USD 1,500" and "USD1500" look
 * identical here). Anything else alphabetic — "abc", "x$" — is left alone and fails the amount
 * match below, so the whole input is rejected rather than silently truncated.
 */
function matchCurrencyPrefix(rest: string): string | null {
  const first = rest[0];
  if (first !== undefined && CURRENCY_SYMBOLS.has(first)) return first;
  const code = rest.slice(0, 3).toUpperCase();
  if (code.length === 3 && /^[A-Za-z]{3}$/.test(rest.slice(0, 3)) && ISO_4217_CODES.has(code)) return rest.slice(0, 3);
  return null;
}

/**
 * The currency an amount without one is shown in: `app_setting.planning_calendar.default_currency`
 * via /api/meta (#147). MetaProvider sets it at boot and whenever Settings change it.
 */
export function setDefaultCurrency(code: string | null | undefined): void {
  if (code && CURRENCY.test(code)) settingsCurrency = code;
}

export function defaultCurrency(): string {
  return settingsCurrency;
}

/**
 * THE money formatter (#78, #147): thousands separators, the currency symbol, cents only when
 * there are cents, US English. A null amount is Unbudgeted, never a zero. A number is accepted
 * for callers that hold one; it is rounded to cents first so it formats exactly like the string.
 */
export function formatMoney(
  amount: string | number | null | undefined,
  currency?: string | null,
  options: MoneyFormatOptions = {},
): string {
  const text = typeof amount === 'number' ? (Number.isFinite(amount) ? amount.toFixed(2) : null) : amount;
  if (!isMoney(text)) return options.unbudgeted ?? UNBUDGETED_LABEL;
  const minor = toMinor(text);
  const code = currency && CURRENCY.test(currency) ? currency : settingsCurrency;
  const cents = Math.abs(minor % 100) > 0;
  const format: Intl.NumberFormatOptions = options.compact
    ? { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol', notation: 'compact', maximumFractionDigits: 1 }
    : {
        style: 'currency',
        currency: code,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: cents ? 2 : 0,
        maximumFractionDigits: cents ? 2 : 0,
      };
  try {
    return new Intl.NumberFormat('en-US', format).format(minor / 100);
  } catch {
    return `${fromMinor(minor)} ${code}`;
  }
}

/**
 * What a person typed in a money field, as the decimal string the API stores — or null when
 * the field is empty, and undefined when it is not a number (#78, A9). Accepts a currency
 * symbol and a leading minus, in either order ("-$1,000", "$-1,000"), and surrounding space.
 * A comma is a thousands separator only in a proper grouping (exactly three digits per group,
 * e.g. "1,500" or "1,250,000") — never a decimal comma; the app's locale is fixed to US
 * English, so "1,5" and "12,50" are invalid, not 1.5x their digits.
 */
export function parseMoneyInput(raw: string): string | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  let rest = trimmed.replace(/\s+/g, '');
  let negative = false;
  let signSeen = false;
  let currencySeen = false;
  // Strip at most one leading sign and at most one currency prefix, in either order. A second
  // of the SAME kind ("--100", "$$100", "USD$100") is not a doubled-up valid amount — it is
  // invalid, so this returns undefined rather than silently accepting it (#78, round 3).
  for (let i = 0; i < 2; i++) {
    if (rest.startsWith('-')) {
      if (signSeen) return undefined;
      signSeen = true;
      negative = true;
      rest = rest.slice(1);
      continue;
    }
    const symbol = matchCurrencyPrefix(rest);
    if (symbol) {
      if (currencySeen) return undefined;
      currencySeen = true;
      rest = rest.slice(symbol.length);
      continue;
    }
    break;
  }
  if (rest === '') return undefined;
  const match = /^(\d{1,3}(?:,\d{3})*|\d+)(\.\d{0,2})?$/.exec(rest);
  if (!match) return undefined;
  const whole = match[1]!.replace(/,/g, '');
  const frac = (match[2] ?? '').slice(1);
  const amount = `${whole}.${frac.padEnd(2, '0')}`;
  return negative ? `-${amount}` : amount;
}

/** Totals grouped per currency — never summed across currencies, never converted (§2). */
export interface CurrencyTotal {
  currency: string;
  amount: string;
}

export function currencyTotals(byCurrency: Record<string, string> | undefined): CurrencyTotal[] {
  return Object.entries(byCurrency ?? {})
    .filter(([currency, amount]) => /^[A-Z]{3}$/.test(currency) && isMoney(amount))
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

export function formatTotals(byCurrency: Record<string, string> | undefined, options?: MoneyFormatOptions): string {
  const totals = currencyTotals(byCurrency);
  if (totals.length === 0) return '—';
  return totals.map((t) => formatMoney(t.amount, t.currency, options)).join(' · ');
}
